import { Routes } from '@angular/router';

import { TaRoutes } from '@ta/menu';
import { AuthGuard, FeatureGuard } from '@ta/user';

export enum EEstatesRoute {
  estates = 'estates',
  list = 'list',
  estate = 'estate',
  myEstate = 'my-estate',
  myEstateForm = 'my-estate-form',
  /** Les locataires dont une recherche correspond à un bien boosté. */
  candidates = 'candidates',
}

/**
 * La fiche d'un bien. Celui qui le publie va sur sa page à lui : `myEstates` n'est jamais filtré,
 * alors que le catalogue public ne montre plus un bien devenu non couvert.
 */
export function estateDetailUrl(estateId: string, isPublisher: boolean): string {
  const page = isPublisher ? EEstatesRoute.myEstate : EEstatesRoute.estate;
  return TaRoutes.getAbsoluteUrl([EEstatesRoute.estates, page], { id: estateId });
}

/** Les candidats d'un bien géré : un onglet de sa fiche de gestion. */
export function myEstateCandidatesUrl(estateId: string): string {
  const page = TaRoutes.getAbsoluteUrl([EEstatesRoute.estates, EEstatesRoute.myEstate], {
    id: estateId,
  });
  return `${page}?tab=candidates`;
}

TaRoutes.addRoute({
  key: EEstatesRoute.estates,
  url: 'estates',
  children: [
    {
      key: EEstatesRoute.list,
      url: 'list',
    },
    {
      key: EEstatesRoute.estate,
      url: 'estate/:id',
    },
    {
      key: EEstatesRoute.myEstate,
      url: 'my-estate/:id',
    },
    {
      key: EEstatesRoute.myEstateForm,
      url: 'my-estate-form/:id',
    },
    {
      key: EEstatesRoute.candidates,
      url: 'candidates/:id',
    },
  ],
});

export const estatesRoutes: Routes = [
  {
    path: TaRoutes.getUrl([EEstatesRoute.estates, EEstatesRoute.list]),
    title: 'app.pages.estates.title',
    loadComponent: () => import('./pages/estates-list/estates-list').then((c) => c.EstatesListPage),
    canActivate: [AuthGuard, FeatureGuard],
    data: { feature: 'estates', level: 'authorize' },
  },
  {
    path: TaRoutes.getUrl([EEstatesRoute.estates, EEstatesRoute.estate]),
    title: 'app.pages.estate_details.title',
    loadComponent: () => import('./pages/estate/estate').then((c) => c.PublicEstatePage),
  },
  {
    path: TaRoutes.getUrl([EEstatesRoute.estates, EEstatesRoute.myEstate]),
    title: 'app.pages.my_estate.title',
    loadComponent: () => import('./pages/my-estate/my-estate').then((c) => c.EstatePage),
    canActivate: [AuthGuard, FeatureGuard],
    data: { feature: 'estates', level: 'authorize' },
  },
  {
    // `new` n'est pas un id : le titre de création passe avant la route paramétrée.
    path: TaRoutes.getUrl([EEstatesRoute.estates, EEstatesRoute.myEstateForm], { id: 'new' }),
    title: 'app.pages.new_estate.title',
    loadComponent: () =>
      import('./pages/my-estate-form/my-estate-form').then((c) => c.EstateFormPage),
    canActivate: [AuthGuard, FeatureGuard],
    data: { feature: 'estates', level: 'authorize' },
  },
  // Les candidats vivent dans un onglet de la fiche du bien ; l'ancienne adresse (liens, notifications) y mène.
  {
    path: TaRoutes.getUrl([EEstatesRoute.estates, EEstatesRoute.candidates]),
    redirectTo: ({ params }) => myEstateCandidatesUrl(params['id']),
  },
  {
    path: TaRoutes.getUrl([EEstatesRoute.estates, EEstatesRoute.myEstateForm]),
    title: 'app.pages.edit_estate.title',
    loadComponent: () =>
      import('./pages/my-estate-form/my-estate-form').then((c) => c.EstateFormPage),
    canActivate: [AuthGuard, FeatureGuard],
    data: { feature: 'estates', level: 'authorize' },
  },
];
